import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getMyInvite, type MyInviteInfo } from '@/api/auth';

export default function InviteFriend() {
  const { user } = useAuth();
  const [info, setInfo] = useState<MyInviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError('');
    getMyInvite()
      .then(setInfo)
      .catch((err) => {
        setInfo(null);
        setError(err instanceof Error ? err.message : '邀请信息加载失败');
      })
      .finally(() => setLoading(false));
  }, [user]);

  const handleCopy = useCallback(async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setter(true);
    setTimeout(() => setter(false), 2000);
  }, []);

  const inviteCode = info?.inviteCode ?? '';
  const inviteLink = inviteCode ? `${window.location.origin}/login?tab=register&ref=${inviteCode}` : '';
  const totalInvited = Number(info?.totalInvited ?? 0);
  const rewarded = Number(info?.rewarded ?? 0);
  const earnedPoints = Number(info?.earnedPoints ?? 0);
  const inviterRewardPoints = info ? Number(info.inviterRewardPoints ?? 0) : null;
  const inviteeRewardPoints = info ? Number(info.inviteeRewardPoints ?? 0) : null;
  const records = info?.records ?? [];
  const inviterRewardText = inviterRewardPoints == null ? '--' : inviterRewardPoints;
  const inviteeRewardText = inviteeRewardPoints == null ? '--' : inviteeRewardPoints;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 flex items-center justify-center bg-[#f97316]/10 rounded-xl">
            <i className="ri-gift-2-line text-[#f97316] text-xl"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">邀请好友</h3>
            <p className="text-xs text-gray-400">好友注册后邀请人 +{inviterRewardText}，被邀请人 +{inviteeRewardText} 灵石</p>
          </div>
        </div>
        <div className="flex items-center justify-center py-10 text-gray-300">
          <div className="w-6 h-6 border-2 border-gray-200 border-t-[#f97316] rounded-full animate-spin mr-2"></div>
          <span className="text-sm">加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-[#f97316]/10 rounded-xl">
          <i className="ri-gift-2-line text-[#f97316] text-xl"></i>
        </div>
        <div>
          <h3 className="font-bold text-gray-900">邀请好友</h3>
          <p className="text-xs text-gray-400">好友注册后邀请人 +{inviterRewardText}，被邀请人 +{inviteeRewardText} 灵石</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-xs text-red-500 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5">
          <i className="ri-error-warning-line text-sm shrink-0"></i>
          <span>{error}</span>
        </div>
      )}

      {/* 邀请码 */}
      <div className="bg-gradient-to-br from-[#fff7ed] to-[#fff0dc] border border-[#f97316]/20 rounded-2xl p-4 mb-4">
        <p className="text-xs text-[#f97316] font-medium mb-2">我的专属邀请码</p>
        {inviteCode ? (
          <>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-white rounded-xl px-4 py-2.5 border border-[#f97316]/20">
                <span className="text-xl font-bold tracking-widest text-gray-900">{inviteCode}</span>
              </div>
              <button
                onClick={() => handleCopy(inviteCode, setCopied)}
                className={`px-4 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  copied ? 'bg-[#10b981] text-white' : 'bg-[#f97316] text-white hover:bg-[#ea6c0a]'
                }`}
              >
                {copied
                  ? <><i className="ri-checkbox-circle-line mr-1"></i>已复制</>
                  : <><i className="ri-clipboard-line mr-1"></i>复制码</>}
              </button>
            </div>
            <button
              onClick={() => handleCopy(inviteLink, setCopiedLink)}
              className={`mt-3 w-full py-2 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                copiedLink
                  ? 'bg-[#10b981]/10 text-[#10b981] border-[#10b981]/20'
                  : 'bg-white text-[#f97316] border-[#f97316]/30 hover:bg-[#f97316]/5'
              }`}
            >
              {copiedLink
                ? <><i className="ri-checkbox-circle-line mr-1.5"></i>邀请链接已复制！</>
                : <><i className="ri-share-line mr-1.5"></i>复制完整邀请链接</>}
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-400 py-2">邀请码生成中，请稍后刷新</p>
        )}
      </div>

      {/* 统计 */}
      <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { label: '已邀请', value: totalInvited, icon: 'ri-user-add-line', color: '#0ea5e9' },
            { label: '已到账', value: rewarded, icon: 'ri-check-double-line', color: '#10b981' },
            { label: '获得灵石', value: info ? `+${earnedPoints}` : '--', icon: 'ri-sparkling-line', color: '#f97316' },
          ].map((s) => (
          <div key={s.label} className="p-3 bg-gray-50 rounded-xl text-center">
            <div
              className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-1"
              style={{ backgroundColor: `${s.color}15` }}
            >
              <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
            </div>
            <div className="text-lg font-bold text-gray-900">{s.value}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* 规则说明 */}
      <div className="p-3 bg-[#0ea5e9]/5 border border-[#0ea5e9]/15 rounded-xl mb-4">
        <p className="text-xs font-semibold text-[#0ea5e9] mb-2 flex items-center gap-1">
          <i className="ri-information-line"></i>邀请规则
        </p>
        <div className="space-y-1.5 text-xs text-gray-600">
          {[
            '复制您的专属邀请码或链接，分享给朋友',
            '朋友注册时填写您的邀请码',
            <>注册成功后，邀请人 <strong className="text-[#f97316]">+{inviterRewardText} 灵石</strong>，被邀请人 <strong className="text-[#f97316]">+{inviteeRewardText} 灵石</strong></>,
          ].map((text, i) => (
            <div key={i} className="flex items-start gap-2">
              <span
                className="w-4 h-4 flex items-center justify-center text-white rounded-full text-xs shrink-0 mt-0.5 font-bold"
                style={{ backgroundColor: i === 2 ? '#10b981' : '#0ea5e9' }}
              >
                {i + 1}
              </span>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 邀请记录 */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <i className="ri-team-line text-[#0ea5e9]"></i>邀请记录
          {totalInvited > 0 && <span className="text-xs text-gray-400">共 {totalInvited} 位</span>}
        </h4>
        {records.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 flex items-center justify-center bg-gray-50 rounded-xl mx-auto mb-3">
              <i className="ri-user-add-line text-2xl text-gray-200"></i>
            </div>
            <p className="text-sm text-gray-400">还没有邀请记录</p>
            <p className="text-xs text-gray-300 mt-1">快把邀请链接分享给朋友吧！</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {records.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 flex items-center justify-center bg-[#0ea5e9]/10 rounded-lg text-[#0ea5e9] font-bold text-sm shrink-0">
                  {r.inviteeName?.charAt(0) ?? '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.inviteeName}</p>
                  <p className="text-xs text-gray-400">
                    {(() => {
                      if (!r.createdAt) return '--';
                      const d = new Date(r.createdAt);
                      return Number.isNaN(d.getTime()) ? '--' : d.toLocaleDateString('zh-CN');
                    })()}
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    r.rewarded ? 'bg-[#10b981]/10 text-[#10b981]' : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {r.rewarded ? `+${r.inviterRewardPoints} 灵石` : '待确认'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
